import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
//import { plainToClass } from "class-transformer";

import { XmlFileFormatEnum } from '../../enums/xml-file-format.enum';
import { IHasIdAndName } from '../../interfaces/has-id-and-name.interface';
import { AsyncJob } from '../../models/async-job.model';
import { DataCollection } from '../../models/site-content/data-collection.model';
import { Folder } from '../../models/site-content/folder.model';
import { Form } from '../../models/form-builder/form.model';
import { AsyncJobService } from '../../services/async-job.service';
//import { CopySiteService } from '../../services/copy-site.service';
import { ImportDataService } from '../../services/import-data.service';
import { CurrentSiteService } from '../../services/current-site.service';
import { ImportAsyncJobDialogBase } from '../async-job-base/import-async-job-base.dialog';
import { SelectFolderDialog } from '../select-folder/select-folder.dialog';
import { ImportFormInstancesMetrics } from '../../models/export-import/import-form-instances-metrics.model';

@Component({
    selector: 'app-import-structure',
    templateUrl: './import-structure.dialog.html',
    styleUrls: [
        '../async-job-base/async-job-base.dialog.scss',
        './import-structure.dialog.scss'
    ],
    standalone: false
})
export class ImportStructureInstancesDialog extends ImportAsyncJobDialogBase implements OnInit {
    // Properties.
    private importedStructureId: number = -1;
    protected targetFolder: Folder = null;
    private targetForm: Form = null;

    // File-related properties.
    @ViewChild('file') file;
    protected fileSet: Set<File> = new Set();
    private selectedFileName: string = null;

    // Folder path-related properties.
    private selectedFolderPath: string = "/";
    private iSelectedFolderId: number = 0;
    protected importFormInstancesMetrics: ImportFormInstancesMetrics = null;

    // XML format properties (not presently being used).
    //private xmlFileFormat: XmlFileFormatEnum = XmlFileFormatEnum.VERBOSE_FORMAT;
    //private useSimplifiedXmlFormat: boolean = false;

    //private checkboxFormControl: FormControl = new FormControl(true); // Not presently being used.

    // Constructor.
    public constructor(public dialogRef: MatDialogRef<ImportStructureInstancesDialog>,
        @Inject(MAT_DIALOG_DATA) public targetObject: IHasIdAndName,
        public dialog: MatDialog,
        private clipboard: Clipboard,
        //protected copySiteService: CopySiteService,
        protected importDataService: ImportDataService,
        private injectedAsyncJobService: AsyncJobService,
        private currentSiteService: CurrentSiteService)
    {
        super(injectedAsyncJobService);

        dialogRef.disableClose = true; // Disable closing the dialogue when the user clicks on the page.

        return;
    }

    // Implement a base class's abstract method.
    public get ComponentClassName(): string {
        return 'ImportStructureInstancesDialog';
    }

    // Life cycle methods.
    public ngOnInit(): void {
        // Did we get a target folder or target form?
        if (this.targetObject != null) {
            if (this.targetObject.typeName() == Folder.TypeName) {
                this.targetFolder = <Folder>this.targetObject;
            } else if (this.targetObject.typeName() == Form.TypeName) {
                this.targetForm = <Form>this.targetObject;
            } else {
                // Error.
                let errorMsg = `ImportStructureDialog:  expected either a target folder or a target form but received neither.`;
                throw(errorMsg);
            }
        }

        return;
    }

    // Override base class methods.
    public AllowMultipleFiles(): boolean {
        return (false);
    }

    public get FolderPathNeeded(): boolean {
        return (this.targetFolder == null);
    }
    public StepNumberText(stepNumber: number): string {
        return (this.targetFolder == null ? `${stepNumber}. ` : '');
    }

    public get SelectedFileName(): string {
        // return (this.selectedFileName != null ? this.selectedFileName : 'No file selected');
        let selectedFileName: string = 'No file selected';

        if (this.fileSet.size == 1) {
            selectedFileName = this.selectedFileName;
        } else if (this.fileSet.size > 1) {
            selectedFileName = `${this.fileSet.size} files selected`;
        }

        return (selectedFileName);
    }
    public get FileSelected(): boolean {
        let bFileSelected: boolean = (this.fileSet.size > 0);

        return (bFileSelected);
    }

    public get SelectedFolderPath(): string {
        return (this.selectedFolderPath != null ? this.selectedFolderPath : 'No location selected');
    }
    public get FolderPathSelected(): boolean {
        let bPathSelected: boolean = ((this.targetFolder != null) || (this.selectedFolderPath != null));

        return (bPathSelected);
    }

    public get ExecutionSummaryText(): string {
        let summaryText: string = super.deriveExecutionSummaryText(this.importFormInstancesMetrics);

        return (summaryText);
    }

    public get DismissButtonTitle(): string {
        //return (this.jobCompleted && (!this.jobErrorOccurred) ? 'Dismiss, Show Imported Forms' : 'Dismiss');
        return ('Dismiss');
    }

    // Handle dialogue action(s).
    public selectImportFile(): void {
        this.file.nativeElement.click(); // Cause the hidden file control to open.

        return;
    }

    public selectImportFolderPath(): void {
        // Process a select folder dialogue.
        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '500px';

        let currentSite: DataCollection = this.currentSiteService.Site;
        let rootFolder: Folder = currentSite.getRootFolder();
        dialogConfig.data = rootFolder;

        const dialogRef = this.dialog.open(SelectFolderDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(selectedFolderResult => {
            if (selectedFolderResult != null) {
                this.selectedFolderPath = selectedFolderResult.name;
                this.iSelectedFolderId = selectedFolderResult.id;
            }
        });
    }

    public onFileAdded(): void {
        // Clear any previous file.
        this.fileSet = new Set();
        this.selectedFileName = null;

        // Save the selected file.
        const files: { [key: string]: File } = this.file.nativeElement.files;

        for (let key in files) {
            if (!isNaN(parseInt(key))) {
                let selectedFile: File = files[key];

                this.fileSet.add(selectedFile);

                if (this.selectedFileName == null) {
                    this.selectedFileName = selectedFile.name; // We will only save the first file's name for now.
                }                
            }
        }

        return;
    }

    public startStructureImport(): void {
        // Initiate the import.
        let arrFiles: File[] = Array.from(this.fileSet);

        if (arrFiles.length > 0) {
            let file: File = arrFiles[0];

            let useSimplifiedXmlFormat: boolean = true; //this.checkboxFormControl.value;
            let xmlFileFormat: XmlFileFormatEnum =
                (useSimplifiedXmlFormat ? XmlFileFormatEnum.SIMPLIFIED_MINIMALIST_FORMAT : XmlFileFormatEnum.VERBOSE_FORMAT);

            if (this.targetFolder != null) {
                //this.copySiteService.importFormInstances(file, xmlFileFormat, this.targetFolder.id).then(asyncJob => {
                this.importDataService.importFormInstances(file, xmlFileFormat, this.targetFolder.id).then(asyncJob => {
                    // Save the returned job key.
                    this.startMonitoringJob(asyncJob, this.jobCompletedOrUpdatedArrowFunction);
                });
            } else {
                //this.copySiteService.importFormInstances(file, xmlFileFormat, this.iSelectedFolderId, this.targetForm.id).then(asyncJob => {
                this.importDataService.importFormInstances(file, xmlFileFormat, this.iSelectedFolderId, this.targetForm.id).then(asyncJob => {
                    // Save the returned job key.
                    this.startMonitoringJob(asyncJob, this.jobCompletedOrUpdatedArrowFunction);
                });
            }
        }

        return;
    }

    public dismissClicked(): void {
        // If we were provided a folder, the imported structure Id is the Id of the imported form.
        // If we were provided a form, the imported structure Is is the Id of the target folder.
        let importedStructureId: number = (this.targetFolder != null ? this.targetFolder.id : this.iSelectedFolderId);
        if (this.jobErrorOccurred) {
            importedStructureId = null;
        }
        this.dialogRef.close(importedStructureId);
    }

    public copyJobErrorTextToClipboard(): void {
        if (this.jobErrorText != null) {
            this.clipboard.copy(this.jobErrorText);
        }
    }

    public cancelClicked(): void {
        this.dialogRef.close(null);
    }

    // Protected methods.
    protected jobCompletedOrUpdatedArrowFunction = (asyncJob: AsyncJob): void => {
        //this.importedStructureId = this.parseResultObjectId(asyncJob);
        // If there is no error, try to parse the job completion summary.
        if ((this.jobCompletedMessageText != null) && (this.jobCompletedMessageText.trim() != '')) {
            //this.importFormInstancesMetrics = ImportStructureInstancesDialog.parseJobMetrics(this.jobCompletedMessageText);
            this.importFormInstancesMetrics = ImportAsyncJobDialogBase.parseImportFormInstancesMetrics(this.jobCompletedMessageText);
        } else if ((asyncJob.stdOut != null) && (asyncJob.stdOut.trim() != '')) {
            //this.importFormInstancesMetrics = ImportStructureInstancesDialog.parseJobMetrics(asyncJob.stdOut);
            this.importFormInstancesMetrics = ImportAsyncJobDialogBase.parseImportFormInstancesMetrics(asyncJob.stdOut);
        }
    }

    /*
    private static parseJobMetrics(jobMetricsJson: string): ImportFormInstancesMetrics {
        let jobCompletedMessageJson: Object = JSON.parse(jobMetricsJson);
        let importMetrics: ImportFormInstancesMetrics = new ImportFormInstancesMetrics(jobCompletedMessageJson);

        return (importMetrics);
    }
    */
}
